import React, { useContext, useState } from "react";
import {
  Button as AntdButton,
  ButtonProps as AntdButtonProps,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  message,
} from "antd";
import { StopOutlined } from "@ant-design/icons";
import { useModal } from "react-modal-hook";
import { useTranslation } from "react-i18next";
import ProductContext from "../../../../context/product/context";
import FormHookModal from "../../modals/form-hook-modal";
import FormItem from "../../form-item";
import { IQuantity } from "../../../../models/product/response";
import EndPoints from "../../../../services/end-points";
import { IBatch } from "../../../../models/wms/batch/response";
import { formatBatchPurchasePriceOf } from "../../../../utils/helpers/batch-purchase-price";
import { BatchWriteOffReason } from "../../../../models/wms/batch/request";

export interface ButtonProps extends AntdButtonProps {
  passedId: number;
  quantities: IQuantity[];
  productName?: string;
}

const getStoreName = (name: string | { ar: string; en: string }): string => {
  if (typeof name === "string") return name;
  return name?.ar || name?.en || "-";
};

const WriteOffBatchButton: React.FC<ButtonProps> = ({
  passedId,
  quantities,
  productName,
  ...props
}) => {
  const { t } = useTranslation();
  const { actions } = useContext(ProductContext);

  const [showFormModal, hideFormModal] = useModal(() => {
    const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [batches, setBatches] = useState<IBatch[]>([]);
    const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
    const [quantity, setQuantity] = useState<number | null>(null);
    const [reason, setReason] = useState<number | null>(null);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const loadBatches = async (storeId: number) => {
      setLoadingBatches(true);
      try {
        const { data } = await EndPoints.wmsBatch.getAllBatches({
          productId: passedId,
          storeId,
          hasStock: true,
          perPage: -1,
        });
        setBatches(data?.data ?? []);
      } catch (error: any) {
        setBatches([]);
      } finally {
        setLoadingBatches(false);
      }
    };

    const handleStoreChange = async (storeId: number) => {
      setSelectedStoreId(storeId);
      setSelectedBatchId(null);
      setQuantity(null);
      setReason(null);
      setNotes("");
      await loadBatches(storeId);
    };

    const selectedBatch = batches.find((batch) => batch.id === selectedBatchId);
    const maxAvailable = Number(selectedBatch?.availableQuantity ?? 0);

    const handleSubmit = async () => {
      if (!selectedBatchId) {
        message.error(t("please_select_batch"));
        return;
      }
      if (!quantity || quantity < 1) {
        message.error(t("write_off_quantity_must_be_at_least_one"));
        return;
      }
      if (quantity > maxAvailable) {
        message.error(t("write_off_quantity_exceeds_available"));
        return;
      }
      if (!reason) {
        message.error(t("please_select_write_off_reason"));
        return;
      }

      setSubmitting(true);
      try {
        await EndPoints.wmsBatch.writeOffBatch(selectedBatchId, {
          quantity,
          reason,
          notes: notes || undefined,
        });
        message.success(t("batch_write_off_successful"));
        actions.getData();
        hideFormModal();
      } catch (error: any) {
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <FormHookModal
        id="write-off-batch-modal"
        title={t("write_off_batch")}
        subtitle={
          productName ? `${t("product_name")}: ${productName}` : undefined
        }
        confirmButtonText={t("confirm")}
        confirmClick={handleSubmit}
        loading={submitting}
        hideModal={hideFormModal}
        width="min(720px, 92vw)"
        bodyScroll
        style={{ marginTop: "1.6rem", maxHeight: "85vh" }}
        onSubmit={() => {}}
      >
        <Row gutter={[12, 12]} style={{ margin: 0, width: "100%" }}>
          <Col span={24}>
            <FormItem label={t("store")}>
              <Select
                style={{ width: "100%" }}
                value={selectedStoreId}
                onChange={handleStoreChange}
                options={quantities.map((q) => ({
                  value: q.store_id,
                  label: getStoreName(q.store_name),
                }))}
              />
            </FormItem>
          </Col>

          {selectedStoreId ? (
            <>
              <Col span={24}>
                <FormItem label={t("batch")}>
                  {loadingBatches ? (
                    <Spin size="small" />
                  ) : (
                    <Select
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%" }}
                      value={selectedBatchId}
                      onChange={(value) => {
                        setSelectedBatchId(value);
                        setQuantity(null);
                      }}
                      options={batches.map((batch) => ({
                        value: batch.id,
                        label: `${batch.batchNumber ?? "#" + batch.id} (${t("available")}: ${batch.availableQuantity}) — ${t("batch_purchase_price")}: ${formatBatchPurchasePriceOf(batch, t("currency"))}`,
                      }))}
                      placeholder={t("select_batch")}
                    />
                  )}
                </FormItem>
              </Col>

              <Col span={24}>
                <FormItem label={t("available_quantity")}>
                  <Input disabled value={maxAvailable || 0} />
                </FormItem>
              </Col>

              <Col span={24}>
                <FormItem label={t("quantity")}>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={1}
                    max={maxAvailable > 0 ? maxAvailable : undefined}
                    value={quantity ?? undefined}
                    onChange={(value) =>
                      setQuantity(
                        value === null || value === undefined
                          ? null
                          : Number(value),
                      )
                    }
                    placeholder={t("write_off_quantity_placeholder")}
                  />
                </FormItem>
              </Col>

              <Col span={24}>
                <FormItem label={t("reason")}>
                  <Select
                    style={{ width: "100%" }}
                    value={reason}
                    onChange={setReason}
                    options={[
                      {
                        value: BatchWriteOffReason.BALANCE_DISCREPANCY,
                        label: t("batch_write_off_reason_balance_discrepancy"),
                      },
                      {
                        value: BatchWriteOffReason.WASTE,
                        label: t("batch_write_off_reason_waste"),
                      },
                      {
                        value: BatchWriteOffReason.SHRINKAGE_EXPENSE,
                        label: t("batch_write_off_reason_shrinkage_expense"),
                      },
                      {
                        value: BatchWriteOffReason.WRITE_OFF,
                        label: t("batch_write_off_reason_write_off"),
                      },
                      {
                        value: BatchWriteOffReason.EXPIRY,
                        label: t("batch_write_off_reason_expiry"),
                      },
                    ]}
                    placeholder={t("please_select_write_off_reason")}
                  />
                </FormItem>
              </Col>

              <Col span={24}>
                <FormItem label={t("note")}>
                  <Input.TextArea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder={t("optional")}
                  />
                </FormItem>
              </Col>
            </>
          ) : null}
        </Row>
      </FormHookModal>
    );
  }, [passedId, quantities, productName]);

  return (
    <AntdButton
      type="primary"
      icon={<StopOutlined />}
      onClick={showFormModal}
      {...props}
    />
  );
};

export default WriteOffBatchButton;
